import { AGENT_LABELS, MODE_LABELS, MODE_PICKER_OPTIONS } from '../constants';

const AGENT_ORDER = ['claude', 'codex', 'opencode', 'genericagent', 'hermes'];
const MODE_ORDER = ['yolo', 'default', 'plan'];

type AgentControlMenuOptions = {
  button: HTMLElement;
  menu: HTMLElement;
  getSelectedAgent: () => string;
  getCurrentMode: () => string;
  onAgentSelect: (agent: string) => void;
  onModeSelect: (mode: string) => void;
};

export type AgentControlMenuApi = {
  update: () => void;
  close: () => void;
};

export function getAgentControlAgentOptions(): Array<{ value: string; label: string }> {
  return AGENT_ORDER.map((value) => ({ value, label: AGENT_LABELS[value] || value }));
}

export function getAgentControlModeOptions(): Array<{ value: string; label: string }> {
  return MODE_ORDER.map((value) => ({
    value,
    label: MODE_PICKER_OPTIONS.find((option) => option.value === value)?.label || MODE_LABELS[value] || value,
  }));
}

function renderGroup(title: string, items: Array<{ value: string; label: string }>, currentValue: string, type: 'agent' | 'mode'): string {
  const attr = type === 'agent' ? 'data-agent-control-agent' : 'data-agent-control-mode';
  return `
    <div class="agent-control-menu-section">
      <div class="agent-control-menu-title">${title}</div>
      <div class="agent-control-menu-options">
        ${items.map((item) => `
          <button type="button" class="agent-control-option${item.value === currentValue ? ' active' : ''}" ${attr}="${item.value}" role="menuitem">
            ${item.label}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

export function setupAgentControlMenu(options: AgentControlMenuOptions): AgentControlMenuApi {
  const { button, menu } = options;
  let open = false;

  function updateExpandedState() {
    button.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
  }

  function render() {
    // 按钮固定显示 Agent；真实状态只在浮层高亮，避免顶部重新膨胀成两个状态文本。
    button.textContent = 'Agent';
    menu.innerHTML = `
      ${renderGroup('Agent', getAgentControlAgentOptions(), options.getSelectedAgent(), 'agent')}
      ${renderGroup('模式', getAgentControlModeOptions(), options.getCurrentMode(), 'mode')}
    `;
  }

  function close() {
    open = false;
    updateExpandedState();
  }

  function toggle() {
    open = !open;
    render();
    updateExpandedState();
  }

  function update() {
    render();
    updateExpandedState();
  }

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    toggle();
  });

  menu.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    if (!(target instanceof HTMLElement)) return;
    const agent = target.dataset.agentControlAgent;
    const mode = target.dataset.agentControlMode;
    if (agent) options.onAgentSelect(agent);
    if (mode) options.onModeSelect(mode);
    close();
  });

  document.addEventListener('click', (event) => {
    if (!open) return;
    const target = event.target;
    if (target instanceof Node && (button.contains(target) || menu.contains(target))) return;
    close();
  });

  document.addEventListener('keydown', (event) => {
    // 只处理菜单打开时的 Esc，避免抢占页面其他快捷键。
    if (!open || event.key !== 'Escape') return;
    close();
    button.focus();
  });

  render();
  updateExpandedState();
  return { update, close };
}
